import { defineStore } from 'pinia';
import { ref } from 'vue';

import {
  getUserInfo,
  loginUser,
  logoutUser,
  registerUser,
  resetUserPassword,
} from '../services/auth.service';
import type { BaseResponse, ResetPassword, User, UserRegister } from 'src/logic/types/all.types';
import { deleteToken, getToken, setToken } from 'src/logic/services/token.service';
import { useClientStore } from 'src/logic/stores/clients.store';
import { chatAuthService } from 'src/logic/services/chat-auth.service';
import { useWSStore } from 'src/logic/stores/ws.store';
import { useUserStatsStore } from 'src/logic/stores/user-stats.store';
import { CHAT_APP_ID } from 'src/logic/constants/chat.constants';

export const useAuthStore = defineStore('auth', () => {
  const clientStore = useClientStore();
  const { fnResetClientStore } = clientStore;
  const wsStore = useWSStore();
  const { closeWS, startWS, isConnected } = wsStore;
  const userStatsStore = useUserStatsStore();
  const { startAnonymous, startAuthorized } = userStatsStore;

  const user = ref<User>();
  const onboardingStatus = ref(false);

  const login = async (username: string, password: string, code: string): Promise<boolean> => {
    const { token, error } = await loginUser(username, password, code);
    if (error) {
      return false;
    }
    setToken(token!);
    return getUser();
  };

  const logout = async (isSendRequest = true): Promise<void> => {
    try {
      if (isSendRequest) {
        await logoutUser();
      }
    } finally {
      deleteToken();
      user.value = undefined;
      fnResetClientStore();
      if (isConnected) {
        closeWS();
      }
      startAnonymous();
    }
  };

  const register = async (
    data: UserRegister,
  ): Promise<{ success: boolean; error: Record<string, string> }> => {
    const { token, error } = await registerUser(data);
    if (error) {
      return { success: false, error: error as Record<string, string> };
    }
    setToken(token!);
    return { success: await getUser(), error: error as Record<string, string> };
  };

  const resetPassword = async (data: ResetPassword): Promise<BaseResponse> => {
    const { success, message, token } = await resetUserPassword(data);
    if (!success && message) {
      return { message, success };
    }
    setToken(token!);
    const isSuccess = await getUser();
    return { success: isSuccess, message };
  };

  const getUser = async (): Promise<boolean> => {
    fnResetClientStore();
    const response = await getUserInfo();
    if (response.error) {
      deleteToken();
      user.value = undefined;
      return false;
    }
    user.value = response.user;
    chatAuthService.initSession(getToken(), CHAT_APP_ID).then();
    startAuthorized().then();
    if (!isConnected) {
      startWS().then();
    }
    return true;
  };

  const getOnboardingStatus = async (): Promise<boolean> => {
    const userData = user.value;
    if (userData) {
      const userType = userData.user_type;

      if (userType && ['agent', 'agency', 'developer'].includes(userType)) {
        const status =
          userData[userType as 'agent' | 'agency' | 'developer']?.onboarding_completed || false;
        onboardingStatus.value = status;

        return status;
      }
    }

    return true;
  };

  return {
    user,
    onboardingStatus,
    login,
    logout,
    getUser,
    register,
    resetPassword,
    getOnboardingStatus,
  };
});
